import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NativeEventEmitter, NativeModules, Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore, useUIStore } from '@/store';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { useTranslation } from '@/hooks/useTranslation';
import {
  findConversationMatches,
  isFindInChatKeyEvent,
  isFindNextKeyEvent,
  resolveConversationFindSubmit,
  shouldDeferFindShortcut,
} from '@/constants/utils/conversation_find';

function ConversationFindBar({ conversationId, messages, execution }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const inputRef = useRef(null);
  const queryRef = useRef('');
  const committedQueryRef = useRef('');
  const matchIndexRef = useRef(0);
  const matchedMessageIdRef = useRef(null);
  const matchesRef = useRef([]);
  const visibleRef = useRef(false);
  const inputFocusedRef = useRef(false);
  const queryForJumpRef = useRef('');

  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState('');
  const [committedQuery, setCommittedQuery] = useState('');
  const [matchIndex, setMatchIndex] = useState(0);

  const pendingFindRequest = useUIStore((state) => state.pendingConversationFindRequest);
  const clearPendingFindRequest = useUIStore((state) => state.clearPendingConversationFindRequest);
  const requestScrollToMessage = useUIStore((state) => state.requestScrollToMessage);
  const setHighlightedMessageId = useUIStore((state) => state.setHighlightedMessageId);
  const clearHighlightedMessageId = useUIStore((state) => state.clearHighlightedMessageId);

  visibleRef.current = visible;
  queryRef.current = query;
  committedQueryRef.current = committedQuery;
  matchIndexRef.current = matchIndex;

  const matches = useMemo(
    () => findConversationMatches(messages, committedQuery),
    [messages, committedQuery],
  );
  matchesRef.current = matches;

  const jumpToMatch = useCallback((index, list, { animated = true } = {}) => {
    if (execution && execution.isCurrent?.() !== true) return;
    if (!conversationId || !list?.length) {
      clearHighlightedMessageId();
      return;
    }
    const bounded = ((index % list.length) + list.length) % list.length;
    const match = list[bounded];
    if (!match?.messageId) return;
    setMatchIndex(bounded);
    matchIndexRef.current = bounded;
    matchedMessageIdRef.current = match.messageId;
    setHighlightedMessageId(match.messageId, committedQueryRef.current.trim(), { persist: true });
    const request = {
      conversationId,
      messageId: match.messageId,
      searchQuery: committedQueryRef.current.trim(),
      isArchivedMessage: match.isArchivedMessage === true,
      archiveOriginalIndex: match.archiveOriginalIndex,
      animated,
      pinAnchor: true,
      viewPosition: 0.35,
    };
    if (execution) execution.scrollToMessage(request);
    else requestScrollToMessage(request);
    const focus = () => inputRef.current?.focus?.();
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(focus);
    } else {
      focus();
    }
  }, [conversationId, clearHighlightedMessageId, requestScrollToMessage, setHighlightedMessageId, execution]);

  const openFind = useCallback((initialQuery = '') => {
    if (execution && execution.isCurrent?.() !== true) return;
    setVisible(true);
    if (typeof initialQuery === 'string' && initialQuery) {
      setQuery(initialQuery);
      queryRef.current = initialQuery;
      setCommittedQuery(initialQuery);
      committedQueryRef.current = initialQuery;
    }
    const focus = () => {
      inputRef.current?.focus?.();
      if (typeof inputRef.current?.select === 'function') inputRef.current.select();
    };
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(focus);
    } else {
      setTimeout(focus, 0);
    }
  }, [execution]);

  const clearCommittedSearch = useCallback(() => {
    setCommittedQuery('');
    committedQueryRef.current = '';
    setMatchIndex(0);
    matchIndexRef.current = 0;
    matchedMessageIdRef.current = null;
    matchesRef.current = [];
    queryForJumpRef.current = '';
    clearHighlightedMessageId();
    execution?.cancelScroll?.();
  }, [clearHighlightedMessageId, execution]);

  const closeFind = useCallback(() => {
    setVisible(false);
    setQuery('');
    queryRef.current = '';
    clearCommittedSearch();
  }, [clearCommittedSearch]);

  const handleQueryChange = useCallback((text) => {
    setQuery(text);
    queryRef.current = text;
    if (!String(text || '').trim()) {
      clearCommittedSearch();
    }
  }, [clearCommittedSearch]);

  const goToNext = useCallback((delta = 1) => {
    const list = matchesRef.current;
    if (!list.length) return;
    jumpToMatch(matchIndexRef.current + delta, list);
  }, [jumpToMatch]);

  const submitFind = useCallback((shiftKey = false) => {
    const decision = resolveConversationFindSubmit({
      draftQuery: queryRef.current,
      committedQuery: committedQueryRef.current,
      shiftKey,
    });
    if (decision.action === 'commit') {
      const next = decision.query;
      setCommittedQuery(next);
      committedQueryRef.current = next;
      if (!next) clearCommittedSearch();
      return;
    }
    goToNext(decision.delta);
  }, [clearCommittedSearch, goToNext]);

  const handleMacKeyPress = useCallback(({ nativeEvent }) => {
    if (nativeEvent.key === 'Escape') closeFind();
  }, [closeFind]);

  useEffect(() => {
    if (!visible) return;
    if (!committedQuery.trim() || !matches.length) {
      execution?.cancelScroll?.();
      clearHighlightedMessageId();
      matchedMessageIdRef.current = null;
      matchIndexRef.current = 0;
      setMatchIndex(0);
      return;
    }
    const queryChanged = queryForJumpRef.current !== committedQuery;
    queryForJumpRef.current = committedQuery;
    if (queryChanged) {
      jumpToMatch(0, matches, { animated: false });
      return;
    }
    if (matches[matchIndexRef.current]?.messageId === matchedMessageIdRef.current) return;
    // Streaming can move an artifact from its tool-only reply to a final
    // answer, or insert matches before the selected message. Track identity.
    const retained = matches.findIndex(match => match.messageId === matchedMessageIdRef.current);
    jumpToMatch(retained >= 0 ? retained : Math.min(matchIndexRef.current, matches.length - 1), matches, { animated: false });
  }, [visible, matches, committedQuery, jumpToMatch, clearHighlightedMessageId, execution]);

  useEffect(() => {
    if (!pendingFindRequest) return;
    if (pendingFindRequest.conversationId !== conversationId) return;
    openFind(pendingFindRequest.query || '');
    clearPendingFindRequest();
  }, [pendingFindRequest, conversationId, openFind, clearPendingFindRequest]);

  useEffect(() => () => {
    if (visibleRef.current) clearHighlightedMessageId();
    execution?.cancelScroll?.();
  }, [clearHighlightedMessageId, execution]);

  useEffect(() => {
    if (Platform.OS !== 'macos' || !NativeModules.MacMenuBridge) return undefined;
    const emitter = new NativeEventEmitter(NativeModules.MacMenuBridge);
    const subscription = emitter.addListener('onMenuAction', ({ action }) => {
      if (useModalStore.getState().isOpen) return;
      if (action === 'findInChat') openFind();
      else if (visibleRef.current && action === 'findNext') goToNext(1);
      else if (visibleRef.current && action === 'findPrevious') goToNext(-1);
    });
    return () => subscription.remove();
  }, [openFind, goToNext]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;

    const handleKeyDown = (event) => {
      if (shouldDeferFindShortcut(event)) return;
      if (useModalStore.getState().isOpen) return;

      if (isFindInChatKeyEvent(event)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
        openFind();
        return;
      }

      if (!visibleRef.current) return;

      if (event.key === 'Escape') {
        // Only claim Escape while the find input holds focus. Elsewhere the key
        // still closes the bar, but menus, popovers and the composer keep theirs.
        if (inputFocusedRef.current) {
          event.preventDefault();
          event.stopPropagation();
        }
        closeFind();
        return;
      }

      if (isFindNextKeyEvent(event)) {
        event.preventDefault();
        event.stopPropagation();
        goToNext(event.shiftKey ? -1 : 1);
        return;
      }

      if (event.key === 'Enter' && inputFocusedRef.current) {
        event.preventDefault();
        submitFind(event.shiftKey);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [openFind, closeFind, goToNext, submitFind]);

  if (!visible) return null;

  const hasCommittedQuery = committedQuery.trim().length > 0;
  const countLabel = !hasCommittedQuery
    ? ''
    : matches.length === 0
      ? t('chat.find.noResults')
      : t('chat.find.matchCount', { current: matchIndex + 1, total: matches.length });

  return (
    <View style={styles.anchor} pointerEvents="box-none">
      <View
        style={styles.bar}
        accessibilityRole="search"
        accessibilityLabel={t('chat.find.placeholder')}
      >
        <Icon name="magnifying-glass" size={13} color={colors.text_color_tertiary} />
        <TextInput
          ref={inputRef}
          value={query}
          onChangeText={handleQueryChange}
          placeholder={t('chat.find.placeholder')}
          placeholderTextColor={colors.color_input_placeholder}
          style={styles.input}
          autoCapitalize="none"
          autoCorrect={false}
          returnKeyType="search"
          blurOnSubmit={false}
          // AppKit's field editor delivers Return through submitEditing, not
          // the view's keyDown callback. Our native patch retains Shift there.
          onSubmitEditing={event => submitFind(Platform.OS === 'macos' && event?.nativeEvent?.shiftKey === true)}
          onKeyPress={Platform.OS === 'macos' ? handleMacKeyPress : undefined}
          onFocus={() => { inputFocusedRef.current = true; }}
          onBlur={() => { inputFocusedRef.current = false; }}
          accessibilityLabel={t('chat.find.placeholder')}
        />
        {hasCommittedQuery ? (
          <Text size="xs" color="tertiary" style={styles.count} numberOfLines={1}>
            {countLabel}
          </Text>
        ) : null}
        <Pressable
          onPress={() => goToNext(-1)}
          disabled={!matches.length}
          style={({ hovered, pressed }) => [
            styles.iconButton,
            (hovered || pressed) && matches.length ? styles.iconButtonHovered : null,
            !matches.length && styles.iconButtonDisabled,
          ]}
          accessibilityRole="button"
          accessibilityLabel={t('chat.find.previous')}
        >
          <Icon name="chevron-up" size={12} color={colors.text_color_secondary} />
        </Pressable>
        <Pressable
          onPress={() => goToNext(1)}
          disabled={!matches.length}
          style={({ hovered, pressed }) => [
            styles.iconButton,
            (hovered || pressed) && matches.length ? styles.iconButtonHovered : null,
            !matches.length && styles.iconButtonDisabled,
          ]}
          accessibilityRole="button"
          accessibilityLabel={t('chat.find.next')}
        >
          <Icon name="chevron-down" size={12} color={colors.text_color_secondary} />
        </Pressable>
        <Pressable
          onPress={closeFind}
          style={({ hovered, pressed }) => [
            styles.iconButton,
            (hovered || pressed) && styles.iconButtonHovered,
          ]}
          accessibilityRole="button"
          accessibilityLabel={t('chat.find.close')}
        >
          <Icon name="xmark" size={12} color={colors.text_color_secondary} />
        </Pressable>
      </View>
    </View>
  );
}

ConversationFindBar.propTypes = {
  conversationId: PropTypes.string,
  messages: PropTypes.array,
  execution: PropTypes.shape({ isCurrent: PropTypes.func.isRequired, scrollToMessage: PropTypes.func.isRequired, cancelScroll: PropTypes.func }),
};

const createStyles = (colors) => StyleSheet.create({
  // The parent messages container lays out in `row`, so an absolute child's
  // alignSelf would center it vertically. Span the width and center inside.
  anchor: {
    position: 'absolute',
    top: 10,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 30,
  },
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingLeft: 12,
    paddingRight: 6,
    height: 40,
    maxWidth: 440,
    width: '90%',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_primary,
  },
  input: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    color: colors.text_color_primary,
    fontSize: 14,
    paddingVertical: 0,
    ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
  },
  count: {
    minWidth: 52,
    textAlign: 'right',
    paddingHorizontal: 4,
  },
  iconButton: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  iconButtonHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  iconButtonDisabled: {
    opacity: 0.35,
    ...(Platform.OS === 'web' && { cursor: 'default' }),
  },
});

export default React.memo(ConversationFindBar);
